import React from 'react';
import { X, Globe, Moon, Bell, Volume2, Image, Play, Calendar, Target } from 'lucide-react';
import { 
  languageDisplayOptions, 
  themeOptions, 
  pronunciationOptions,
  dailyGoalOptions,
  chineseCharacterOptions,
  getLanguageDisplayLabel, 
  getThemeLabel,
  getPronunciationLabel,
  getChineseCharacterLabel
} from '../data/userSettings';

const Settings = ({ 
  settings, 
  onSettingsChange, 
  onClose,
  className = "" 
}) => {
  const handleLanguageDisplayChange = (value) => {
    onSettingsChange({
      ...settings,
      languageDisplay: value
    });
  };

  const handleChineseCharacterChange = (value) => {
    onSettingsChange({
      ...settings,
      chineseCharacterPreference: value
    });
  };

  const handleThemeChange = (value) => {
    onSettingsChange({
      ...settings,
      theme: value
    });
  };

  const handlePronunciationChange = (key, value) => {
    onSettingsChange({
      ...settings,
      pronunciation: {
        ...settings.pronunciation,
        [key]: value
      }
    });
  };

  const handleNotificationChange = (key, value) => {
    onSettingsChange({
      ...settings,
      notifications: {
        ...settings.notifications,
        [key]: value
      }
    });
  };

  const handleStudyPreferenceChange = (key, value) => {
    onSettingsChange({
      ...settings,
      studyPreferences: {
        ...settings.studyPreferences,
        [key]: value
      }
    });
  };

  const handleLearningScheduleChange = (key, value) => {
    onSettingsChange({
      ...settings,
      learningSchedule: {
        ...settings.learningSchedule,
        [key]: value
      }
    });
  };

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${className}`}>
      <div className="bg-white rounded-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">Settings</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Language Display */}
          <div>
            <div className="flex items-center mb-3">
              <Globe className="w-5 h-5 text-blue-500 mr-2" />
              <h3 className="font-semibold text-gray-800">Language Display</h3>
            </div>
            <div className="space-y-2">
              {languageDisplayOptions.map((option) => (
                <label key={option.value} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="languageDisplay"
                    value={option.value}
                    checked={settings.languageDisplay === option.value}
                    onChange={(e) => handleLanguageDisplayChange(e.target.value)}
                    className="mr-3"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Chinese Character Preference */}
          <div>
            <div className="flex items-center mb-3">
              <Image className="w-5 h-5 text-purple-500 mr-2" />
              <h3 className="font-semibold text-gray-800">Chinese Character Preference</h3>
            </div>
            <div className="space-y-2">
              {chineseCharacterOptions.map((option) => (
                <label key={option.value} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="chineseCharacterPreference"
                    value={option.value}
                    checked={settings.chineseCharacterPreference === option.value}
                    onChange={(e) => handleChineseCharacterChange(e.target.value)}
                    className="mr-3"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Pronunciation Settings */}
          <div>
            <div className="flex items-center mb-3">
              <Volume2 className="w-5 h-5 text-green-500 mr-2" />
              <h3 className="font-semibold text-gray-800">Pronunciation</h3>
            </div>
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">Enable Pronunciation</span>
                <input
                  type="checkbox"
                  checked={settings.pronunciation.enabled}
                  onChange={(e) => handlePronunciationChange('enabled', e.target.checked)}
                  className="ml-3"
                />
              </label>
              
              {settings.pronunciation.enabled && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Pronunciation Language</label>
                    {pronunciationOptions.map((option) => (
                      <label key={option.value} className="flex items-center p-2 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="pronunciationLanguage"
                          value={option.value}
                          checked={settings.pronunciation.language === option.value}
                          onChange={(e) => handlePronunciationChange('language', e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                  
                  <label className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="text-sm">Auto-play Audio</span>
                    <input
                      type="checkbox"
                      checked={settings.pronunciation.autoPlay}
                      onChange={(e) => handlePronunciationChange('autoPlay', e.target.checked)}
                      className="ml-3"
                    />
                  </label>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700">Volume</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={settings.pronunciation.volume}
                      onChange={(e) => handlePronunciationChange('volume', parseFloat(e.target.value))}
                      className="w-full mt-2"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      {Math.round(settings.pronunciation.volume * 100)}%
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Learning Schedule */}
          <div>
            <div className="flex items-center mb-3">
              <Calendar className="w-5 h-5 text-purple-500 mr-2" />
              <h3 className="font-semibold text-gray-800">Learning Schedule</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Daily Goal</label>
                <div className="space-y-2 mt-2">
                  {dailyGoalOptions.map((option) => (
                    <label key={option.value} className="flex items-center p-2 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="dailyGoal"
                        value={option.value}
                        checked={settings.learningSchedule.dailyGoal === option.value}
                        onChange={(e) => handleLearningScheduleChange('dailyGoal', parseInt(e.target.value))}
                        className="mr-2"
                      />
                      <span className="text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Preferred Study Time</label>
                <input
                  type="time"
                  value={settings.learningSchedule.preferredTime}
                  onChange={(e) => handleLearningScheduleChange('preferredTime', e.target.value)}
                  className="w-full p-2 border rounded-lg mt-1"
                />
              </div>
              
              <label className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">Auto-assign Sets</span>
                <input
                  type="checkbox"
                  checked={settings.learningSchedule.autoAssign}
                  onChange={(e) => handleLearningScheduleChange('autoAssign', e.target.checked)}
                  className="ml-3"
                />
              </label>
            </div>
          </div>

          {/* Theme */}
          <div>
            <div className="flex items-center mb-3">
              <Moon className="w-5 h-5 text-purple-500 mr-2" />
              <h3 className="font-semibold text-gray-800">Theme</h3>
            </div>
            <div className="space-y-2">
              {themeOptions.map((option) => (
                <label key={option.value} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="theme"
                    value={option.value}
                    checked={settings.theme === option.value}
                    onChange={(e) => handleThemeChange(e.target.value)}
                    className="mr-3"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Notifications */}
          <div>
            <div className="flex items-center mb-3">
              <Bell className="w-5 h-5 text-orange-500 mr-2" />
              <h3 className="font-semibold text-gray-800">Notifications</h3>
            </div>
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">Daily Reminder</span>
                <input
                  type="checkbox"
                  checked={settings.notifications.dailyReminder}
                  onChange={(e) => handleNotificationChange('dailyReminder', e.target.checked)}
                  className="ml-3"
                />
              </label>
              <label className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">Sound</span>
                <input
                  type="checkbox"
                  checked={settings.notifications.soundEnabled}
                  onChange={(e) => handleNotificationChange('soundEnabled', e.target.checked)}
                  className="ml-3"
                />
              </label>
              <label className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">Vibration</span>
                <input
                  type="checkbox"
                  checked={settings.notifications.vibrationEnabled}
                  onChange={(e) => handleNotificationChange('vibrationEnabled', e.target.checked)}
                  className="ml-3"
                />
              </label>
            </div>
          </div>

          {/* Study Preferences */}
          <div>
            <div className="flex items-center mb-3">
              <Play className="w-5 h-5 text-green-500 mr-2" />
              <h3 className="font-semibold text-gray-800">Study Preferences</h3>
            </div>
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">Show Images</span>
                <input
                  type="checkbox"
                  checked={settings.studyPreferences.showImages}
                  onChange={(e) => handleStudyPreferenceChange('showImages', e.target.checked)}
                  className="ml-3"
                />
              </label>
              <label className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">Show Phonetics</span>
                <input
                  type="checkbox"
                  checked={settings.studyPreferences.showPhonetics}
                  onChange={(e) => handleStudyPreferenceChange('showPhonetics', e.target.checked)}
                  className="ml-3"
                />
              </label>
              <label className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">Card Animation</span>
                <input
                  type="checkbox"
                  checked={settings.studyPreferences.cardAnimation}
                  onChange={(e) => handleStudyPreferenceChange('cardAnimation', e.target.checked)}
                  className="ml-3"
                />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
